import { useCallback, useEffect, useRef, useState } from 'react';
import { VariableSizeList } from 'react-window';

const ITEM_HEIGHT = 52;
const MAX_HEIGHT = 300;

const Row = ({
  index,
  style,
  children,
  onMouseDown,
  measureRowHeight,
  ...props
}: any) => {
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = rowRef.current;
    if (!element) return;

    const rafId = requestAnimationFrame(() => {
      measureRowHeight(index, element.offsetHeight);
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [index, measureRowHeight]);

  return (
    <div
      {...props}
      style={{
        ...style,
        height: 'fit-content',
        padding: 0,
      }}
      onMouseDown={onMouseDown}
      ref={rowRef}
    >
      {children[index]}
    </div>
  );
};

export const VirtualListbox = ({ ...props }) => {
  const listRef = useRef<VariableSizeList>(null);
  const sizeMap = useRef<Record<number, number>>({});

  const len = props.children.length;

  const initialHeight = Math.min(len * ITEM_HEIGHT, MAX_HEIGHT);

  const [containerHeight, setContainerHeight] = useState(() => initialHeight);

  const getItemSize = (index: number) => sizeMap.current[index] || ITEM_HEIGHT;

  const getTotalHeight = useCallback(() => {
    const currentSizeMap = sizeMap.current;
    let total = 0;
    for (let i = 0; i < len; i++) {
      total += currentSizeMap[i] || ITEM_HEIGHT;
      if (total > MAX_HEIGHT) {
        return MAX_HEIGHT;
      }
    }
    return total;
  }, [len]);

  useEffect(() => {
    setContainerHeight(getTotalHeight());
  }, [getTotalHeight]);

  const measureRowHeight = useCallback(
    (index: number, height: number) => {
      if (height === sizeMap.current[index]) return;
      sizeMap.current[index] = height;
      listRef.current?.resetAfterIndex(index);
      const newContainerHeight = getTotalHeight();
      if (newContainerHeight !== containerHeight) {
        setContainerHeight(newContainerHeight);
      }
    },
    [getTotalHeight, containerHeight]
  );
  return (
    <VariableSizeList
      height={containerHeight + 1}
      width="100%"
      itemCount={len}
      itemSize={getItemSize}
      overscanCount={15}
      ref={listRef}
    >
      {({ index, style }) => (
        <Row
          {...props}
          index={index}
          style={style}
          measureRowHeight={measureRowHeight}
        />
      )}
    </VariableSizeList>
  );
};
